import { Injectable, inject } from '@angular/core';
import {
  ConfigurationSetting,
  ConfigurationSettingCodes,
} from '@maximizer/core/shared/domain';
import { OutlookStore } from '../state';
import {
  ConfigurationService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import {
  catchError,
  defer,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { UserSyncConfiguration } from '@maximizer/outlook-sync/data-access';
import { OutlookPreferences } from '../models';

@Injectable()
export class PreferencesService {
  preferencesSettings: ConfigurationSetting | null = null;
  outlookStore = inject(OutlookStore);
  globalStore = inject(GlobalStore);
  configurationService = inject(ConfigurationService);

  defaultPreferences: OutlookPreferences = {
    saveEmailDetail: false,
    saveNow: false,
    saveAppointmentDetail: false,
  };

  private pendingWrite: Observable<ConfigurationSetting | null> | null = null;

  getPreferences(): Observable<ConfigurationSetting | null> {
    return defer(() => {
      const userId = this.currentUserId();
      if (!userId) {
        return of(null);
      }

      return this.configurationService.getSetting(this.codesFor(userId)).pipe(
        tap((setting) => {
          if (this.currentUserId() !== userId) return;
          this.cacheSetting(setting, userId);
          this.outlookStore.setPreferences(
            setting?.textValue
              ? (JSON.parse(setting.textValue) as OutlookPreferences)
              : { ...this.defaultPreferences },
          );
        }),
      );
    });
  }

  /**
   * One-time "Save on send" default for users whose sync was installed after
   * their preferences were auto-created and never edited since. Persisting
   * saveNow=false advances LastModifyDate, so the override can never re-fire.
   */
  applySaveOnSendDefault(
    syncConfig?: UserSyncConfiguration,
  ): Observable<ConfigurationSetting | null> {
    const setting = this.cachedSettingFor(this.currentUserId());

    if (
      !setting?.creationDate ||
      !setting.lastModifyDate ||
      !syncConfig?.enabled ||
      !syncConfig.createdAt
    ) {
      return of(null);
    }

    const neverEdited = setting.creationDate === setting.lastModifyDate;
    const syncInstalledAfter =
      new Date(syncConfig.createdAt) > new Date(setting.lastModifyDate);

    if (!neverEdited || !syncInstalledAfter) {
      return of(null);
    }

    return this.updatePreferences({
      ...this.outlookStore.preferences(),
      saveNow: false,
    });
  }

  updatePreferences(
    preferences: OutlookPreferences,
  ): Observable<ConfigurationSetting | null> {
    return defer(() => {
      const userId = this.currentUserId();
      if (!userId) {
        return of(null);
      }

      const write: Observable<ConfigurationSetting | null> = (
        this.pendingWrite ?? of(null)
      ).pipe(
        catchError(() => of(null)),
        switchMap(() => this.writeSetting(preferences, userId)),
        finalize(() => {
          if (this.pendingWrite === write) {
            this.pendingWrite = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.pendingWrite = write;
      return write;
    });
  }

  private writeSetting(
    preferences: OutlookPreferences,
    userId: string,
  ): Observable<ConfigurationSetting | null> {
    const codes = this.codesFor(userId);
    return this.resolveSettingId(userId, codes).pipe(
      switchMap((id) =>
        this.configurationService.updateSetting(
          id,
          codes,
          JSON.stringify(preferences),
        ),
      ),
      tap((newSetting) => {
        if (!newSetting || this.currentUserId() !== userId) return;
        this.cacheSetting(newSetting, userId);
        this.outlookStore.setPreferences(preferences);
      }),
    );
  }

  /**
   * Without an id the write becomes a create, which collides with the existing record.
   * A cached id is only reusable for the user it was read for.
   */
  private resolveSettingId(
    userId: string,
    codes: ConfigurationSettingCodes,
  ): Observable<string | null> {
    const storedId =
      this.outlookStore.preferencesConfigurationUserId() === userId
        ? this.outlookStore.preferencesConfigurationId()
        : null;
    const known = this.cachedSettingFor(userId)?.id ?? storedId;
    if (known) {
      return of(known);
    }
    return this.configurationService.getSetting(codes).pipe(
      map((setting) => {
        if (setting && this.currentUserId() === userId) {
          this.cacheSetting(setting, userId);
        }
        return setting?.id ?? null;
      }),
    );
  }

  private currentUserId(): string {
    return this.globalStore.session()?.user.id ?? '';
  }

  /** The setting Key is the Code1-4 composite, so writing without code2 targets another record. */
  private codesFor(userId: string): ConfigurationSettingCodes {
    return {
      code2: userId,
      code3: 'OutlookPreferences',
      code4: 'Settings',
    };
  }

  private cachedSettingFor(userId: string): ConfigurationSetting | null {
    if (!userId) return null;
    return this.preferencesSettings?.code2 === userId
      ? this.preferencesSettings
      : null;
  }

  /** Stamp the owner ourselves so ownership never depends on the backend echoing Code2 back. */
  private cacheSetting(
    setting: ConfigurationSetting | null,
    userId: string,
  ): void {
    this.preferencesSettings = setting ? { ...setting, code2: userId } : null;
    this.outlookStore.storePreferencesConfigurationId(
      setting?.id ?? '',
      userId,
    );
  }
}
